import React, { useEffect, useState, useMemo } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useSortableData } from '../hooks/useSortableData';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ShoppingCart,
  Plus,
  Filter,
  Calendar,
  Building2,
  Package,
  CalendarDays,
  X,
  ArrowUpDown,
  Trash2,
  Search,
  Clock,
  ChevronRight
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS_STORAGE_KEY = 'horecaplat_orders_filters';

// Carica filtri salvati da localStorage
const loadSavedFilters = () => {
  try {
    const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (e) {
    console.warn('Errore caricamento filtri salvati:', e);
  }
  return { statusFilter: 'all', dateFrom: '', dateTo: '' };
};

const OrdersPage = () => {
  const { isBuyer, isSeller } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtro per cliente (da URL param buyer_company_id)
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const buyerCompanyIdFilter = urlParams.get('buyer_company_id') || '';
  const [buyerCompanyName, setBuyerCompanyName] = useState('');
  
  // Modalità groupage - nasconde "Nuovo Ordine" se attiva
  const [isGroupageMode, setIsGroupageMode] = useState(false);
  const [canAccessCatalog, setCanAccessCatalog] = useState(true);
  
  // Carica filtri da localStorage al mount
  const savedFilters = loadSavedFilters();
  const [statusFilter, setStatusFilter] = useState(savedFilters.statusFilter);
  const [dateFrom, setDateFrom] = useState(savedFilters.dateFrom);
  const [dateTo, setDateTo] = useState(savedFilters.dateTo);
  
  const [relationships, setRelationships] = useState<any[]>([]);
  const [showNewOrderDialog, setShowNewOrderDialog] = useState(false);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'standard' | 'groupage'>('all');
  
  // Dialog conferma eliminazione
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Conteggio ordini groupage in attesa
  const pendingGroupageOrders = useMemo(() => {
    return orders.filter(o => o.type === 'groupage' && o.status === 'pending');
  }, [orders]);
  
  // Filtra per tipo prima del sort
  const filteredByType = useMemo(() => {
    if (typeFilter === 'all') return orders;
    return orders.filter(o => (o.type || 'standard') === typeFilter);
  }, [orders, typeFilter]);
  
  // Hook per ordinamento tabella
  const { sortedData: sortedOrders, requestSort, getSortIcon, getSortClass } = useSortableData(
    filteredByType, 
    { key: 'created_at', direction: 'desc' }
  );

  // Filtra e ordina fornitori per la ricerca
  const filteredSuppliers = useMemo(() => {
    if (!relationships.length) return { recent: [], others: [] };
    
    // Ordina per ultimo ordine (i più recenti prima)
    const sorted = [...relationships].sort((a, b) => {
      const dateA = a.last_order_at ? new Date(a.last_order_at).getTime() : 0;
      const dateB = b.last_order_at ? new Date(b.last_order_at).getTime() : 0;
      return dateB - dateA;
    });
    
    // Filtra per ricerca
    const searchLower = supplierSearch.toLowerCase();
    const filtered = sorted.filter(rel => 
      rel.partner_company?.name?.toLowerCase().includes(searchLower)
    );
    
    // Separa recenti (ultimi 30 giorni con ordini) dagli altri
    const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
    const recent = filtered.filter(rel => 
      rel.last_order_at && new Date(rel.last_order_at).getTime() > thirtyDaysAgo
    );
    const others = filtered.filter(rel => 
      !rel.last_order_at || new Date(rel.last_order_at).getTime() <= thirtyDaysAgo
    );
    
    return { recent, others };
  }, [relationships, supplierSearch]);

  // Salva filtri in localStorage quando cambiano
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify({
        statusFilter,
        dateFrom,
        dateTo
      }));
    } catch (e) {
      console.warn('Errore salvataggio filtri:', e);
    }
  }, [statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchOrders();
    if (isBuyer) {
      fetchRelationships();
      checkGroupageMode();
    }
  }, [statusFilter, dateFrom, dateTo, buyerCompanyIdFilter]);

  // Verifica se l'utente è in modalità groupage-only o buyer+associato
  const checkGroupageMode = async () => {
    try {
      const res = await axios.get(`${API}/associations/my-association/menu`);
      if (res.data.is_association_user) {
        const menu = res.data.menu || {};
        const canAccessDir = menu.directory_fornitori?.visible === true;
        const canAccessFornitori = menu.fornitori?.visible === true;
        // Modalità groupage-only: SOLO se directory e fornitori sono nascosti
        // Se ha accesso alla directory, può fare anche acquisti tradizionali
        setIsGroupageMode(!canAccessDir && !canAccessFornitori);
        setCanAccessCatalog(canAccessDir);
      }
    } catch {
      // Non è utente associazione
      setIsGroupageMode(false);
      setCanAccessCatalog(true);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (buyerCompanyIdFilter) params.append('buyer_company_id', buyerCompanyIdFilter);
      
      const res = await axios.get(`${API}/orders?${params.toString()}`);
      setOrders(res.data);
      
      // Se filtro attivo, recupera il nome del cliente
      if (buyerCompanyIdFilter && res.data.length > 0) {
        const companyName = res.data[0]?.partner_company?.name || '';
        setBuyerCompanyName(companyName);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationships = async () => {
    try {
      const res = await axios.get(`${API}/relationships`);
      setRelationships(res.data.filter(r => r.status === 'active'));
    } catch (error) {
      console.error('Error fetching relationships:', error);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-muted text-muted-foreground',
      submitted: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20',
      confirmed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
      in_preparation: 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20',
      ready: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20',
      completed: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
      cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors[status] || colors.draft}`}>
        {t(`orders.${status}`)}
      </span>
    );
  };

  const handleConfirmOrder = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/confirm`);
      toast.success(t('orders.confirmOrder'));
      fetchOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  const handleSubmitOrder = async (orderId) => {
    try {
      await axios.post(`${API}/orders/${orderId}/submit`);
      toast.success(t('orders.submitOrder'));
      fetchOrders();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    }
  };

  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/orders/${orderToDelete}`);
      toast.success('Bozza eliminata');
      fetchOrders();
      // Dispatch event per aggiornare il badge nel layout
      window.dispatchEvent(new CustomEvent('order-updated'));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nell\'eliminazione'));
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setOrderToDelete(null);
    }
  };

  const openDeleteDialog = (orderId: string) => {
    setOrderToDelete(orderId);
    setShowDeleteDialog(true);
  };

  const handleSelectSupplier = (relationshipId: string) => {
    setShowNewOrderDialog(false);
    setSupplierSearch('');
    navigate(`/orders/new?relationship=${relationshipId}`);
  };

  const formatLastOrder = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Oggi';
    if (diffDays === 1) return 'Ieri';
    if (diffDays < 7) return `${diffDays} giorni fa`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} settimane fa`;
    return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t('orders.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {isBuyer ? 'I tuoi ordini ai fornitori' : 'Ordini ricevuti dai clienti'}
          </p>
        </div>
        {isBuyer && !isGroupageMode && (
          <Button 
            onClick={() => setShowNewOrderDialog(true)}
            data-testid="new-order-button"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('orders.newOrder')}
          </Button>
        )}
      </div>

      {/* Banner filtro per cliente */}
      {buyerCompanyIdFilter && isSeller && (
        <div className="flex items-center gap-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-3" data-testid="buyer-filter-banner">
          <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-sm text-blue-800 dark:text-blue-200 font-medium">
              Ordini di: {buyerCompanyName || 'cliente'}
            </span>
            <span className="text-xs text-blue-500 dark:text-blue-400 block">Filtro attivo - stai vedendo solo gli ordini di questo cliente</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 dark:hover:bg-blue-900/50 flex-shrink-0"
            onClick={() => navigate('/orders')}
          >
            <X className="h-4 w-4 mr-1" />
            Rimuovi filtro
          </Button>
        </div>
      )}

      {/* Banner Ordini Groupage in Attesa (solo seller) */}
      {isSeller && pendingGroupageOrders.length > 0 && (
        <Card className="bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900 rounded-lg">
                  <Package className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800 dark:text-emerald-200">
                    {pendingGroupageOrders.length} {pendingGroupageOrders.length === 1 ? 'ordine Groupage' : 'ordini Groupage'} in attesa
                  </p>
                  <p className="text-sm text-emerald-600 dark:text-emerald-400">
                    Ordini collettivi da associazioni - richiedono conferma
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                onClick={() => setTypeFilter('groupage')}
              >
                Visualizza
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Filtro Tipo (solo seller) */}
        {isSeller && (
          <Select value={typeFilter} onValueChange={(v: 'all' | 'standard' | 'groupage') => setTypeFilter(v)}>
            <SelectTrigger className="w-40" data-testid="order-type-filter">
              <Package className="mr-2 h-4 w-4" />
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="groupage">
                <span className="flex items-center gap-2">
                  Groupage
                  {pendingGroupageOrders.length > 0 && (
                    <Badge variant="secondary" className="text-xs">{pendingGroupageOrders.length}</Badge>
                  )}
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
        )}

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48" data-testid="order-status-filter">
            <Filter className="mr-2 h-4 w-4" />
            <SelectValue placeholder={t('common.filter')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="draft">{t('orders.draft')}</SelectItem>
            <SelectItem value="pending">In attesa</SelectItem>
            <SelectItem value="submitted">{t('orders.submitted')}</SelectItem>
            <SelectItem value="confirmed">{t('orders.confirmed')}</SelectItem>
            <SelectItem value="in_preparation">{t('orders.in_preparation')}</SelectItem>
            <SelectItem value="ready">{t('orders.ready')}</SelectItem>
            <SelectItem value="completed">{t('orders.completed')}</SelectItem>
            <SelectItem value="cancelled">{t('orders.cancelled')}</SelectItem>
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2" data-testid="date-filter-btn">
              <CalendarDays className="h-4 w-4" />
              {dateFrom || dateTo ? (
                <span className="text-sm">
                  {dateFrom || '...'} - {dateTo || '...'}
                </span>
              ) : (
                t('orders.filterByDate')
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="start">
            <div className="space-y-4">
              {/* Preset rapidi */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide">Filtri rapidi</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      const today = new Date();
                      const weekAgo = new Date(today);
                      weekAgo.setDate(today.getDate() - 7);
                      setDateFrom(weekAgo.toISOString().split('T')[0]);
                      setDateTo(today.toISOString().split('T')[0]);
                    }}
                    data-testid="preset-week"
                  >
                    Ultima settimana
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      const today = new Date();
                      const monthAgo = new Date(today);
                      monthAgo.setMonth(today.getMonth() - 1);
                      setDateFrom(monthAgo.toISOString().split('T')[0]);
                      setDateTo(today.toISOString().split('T')[0]);
                    }}
                    data-testid="preset-month"
                  >
                    Ultimo mese
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      const today = new Date();
                      const threeMonthsAgo = new Date(today);
                      threeMonthsAgo.setMonth(today.getMonth() - 3);
                      setDateFrom(threeMonthsAgo.toISOString().split('T')[0]);
                      setDateTo(today.toISOString().split('T')[0]);
                    }}
                    data-testid="preset-3months"
                  >
                    Ultimi 3 mesi
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="justify-start"
                    onClick={() => {
                      const today = new Date();
                      setDateFrom(today.toISOString().split('T')[0]);
                      setDateTo(today.toISOString().split('T')[0]);
                    }}
                    data-testid="preset-today"
                  >
                    Oggi
                  </Button>
                </div>
              </div>

              <div className="border-t pt-4">
                <Label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">Personalizzato</Label>
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-sm">{t('orders.dateFrom')}</Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      data-testid="date-from-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">{t('orders.dateTo')}</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      data-testid="date-to-input"
                    />
                  </div>
                </div>
              </div>

              {(dateFrom || dateTo) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full"
                  onClick={() => { setDateFrom(''); setDateTo(''); }}
                >
                  <X className="mr-2 h-4 w-4" />
                  {t('orders.removeDateFilter')}
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {(statusFilter !== 'all' || dateFrom || dateTo) && (
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => { 
              setStatusFilter('all'); 
              setDateFrom(''); 
              setDateTo(''); 
              // Pulisce anche localStorage
              localStorage.removeItem(FILTERS_STORAGE_KEY);
            }}
          >
            <X className="mr-2 h-4 w-4" />
            {t('orders.resetFilters')}
          </Button>
        )}
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : sortedOrders.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('id')}
                  >
                    <span className="flex items-center gap-1">
                      {t('orders.orderNumber')}
                      <span className={getSortClass('id')}>{getSortIcon('id')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('partner_company_name')}
                  >
                    <span className="flex items-center gap-1">
                      {isBuyer ? t('orders.supplier') : t('orders.customer')}
                      <span className={getSortClass('partner_company_name')}>{getSortIcon('partner_company_name')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('created_at')}
                  >
                    <span className="flex items-center gap-1">
                      {t('common.date')}
                      <span className={getSortClass('created_at')}>{getSortIcon('created_at')}</span>
                    </span>
                  </TableHead>
                  <TableHead className="uppercase text-xs">{t('orders.items')}</TableHead>
                  <TableHead 
                    className="uppercase text-xs text-right cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('total')}
                  >
                    <span className="flex items-center justify-end gap-1">
                      {t('common.total')}
                      <span className={getSortClass('total')}>{getSortIcon('total')}</span>
                    </span>
                  </TableHead>
                  <TableHead 
                    className="uppercase text-xs cursor-pointer hover:bg-muted/50 select-none"
                    onClick={() => requestSort('status')}
                  >
                    <span className="flex items-center gap-1">
                      {t('common.status')}
                      <span className={getSortClass('status')}>{getSortIcon('status')}</span>
                    </span>
                  </TableHead>
                  <TableHead className="uppercase text-xs">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOrders.map((order) => (
                  <TableRow 
                    key={order.id}
                    className={`cursor-pointer hover:bg-muted/50 ${order.type === 'groupage' ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''}`}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    data-testid={`order-row-${order.id}`}
                  >
                    <TableCell className="font-mono text-sm">
                      <div className="flex flex-col gap-1">
                        <span>#{order.order_number || order.id.slice(0, 8)}</span>
                        {order.type === 'groupage' && (
                          <Badge variant="outline" className="w-fit text-xs bg-emerald-100 text-emerald-700 border-emerald-300">
                            <Package className="h-3 w-3 mr-1" />
                            Groupage
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <span>{order.partner_company?.name || order.buyer_company_name || 'N/A'}</span>
                        </div>
                        {order.type === 'groupage' && (
                          <span className="text-xs text-emerald-600 ml-6">
                            da <strong>{order.association_name || 'Associazione'}</strong>
                            {order.groupage_title && <span className="text-emerald-500"> • {order.groupage_title}</span>}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        {order.items?.length || order.lines?.length || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(order.total)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(order.status)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {isBuyer && order.status === 'draft' && (
                          <>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={() => openDeleteDialog(order.id)}
                              data-testid={`delete-order-${order.id}`}
                              title="Elimina bozza"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              onClick={() => handleSubmitOrder(order.id)}
                              data-testid={`submit-order-${order.id}`}
                            >
                              {t('orders.submitOrder')}
                            </Button>
                          </>
                        )}
                        {isSeller && (order.status === 'submitted' || order.status === 'pending') && (
                          <Button 
                            size="sm"
                            onClick={() => handleConfirmOrder(order.id)}
                            data-testid={`confirm-order-${order.id}`}
                          >
                            {t('orders.confirmOrder')}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <ShoppingCart className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {isGroupageMode ? 'Utilizza il Groupage per effettuare ordini' : t('orders.noOrders')}
            </p>
            {isBuyer && !isGroupageMode && (
              <Button 
                className="mt-4"
                onClick={() => setShowNewOrderDialog(true)}
              >
                <Plus className="mr-2 h-4 w-4" />
                {t('orders.newOrder')}
              </Button>
            )}
            {isGroupageMode && (
              <Button 
                className="mt-4"
                onClick={() => navigate('/groupage')}
              >
                <Package className="mr-2 h-4 w-4" />
                Vai al Groupage
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* New Order Dialog - Lista con Ricerca */}
      <Dialog open={showNewOrderDialog} onOpenChange={(open) => {
        setShowNewOrderDialog(open);
        if (!open) setSupplierSearch('');
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('orders.newOrder')}</DialogTitle>
            <DialogDescription>
              Seleziona un fornitore per iniziare
            </DialogDescription>
          </DialogHeader>
          
          {/* Barra di ricerca */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca fornitore..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              className="pl-10"
              data-testid="supplier-search-input"
            />
          </div>
          
          {relationships.length > 0 ? (
            <ScrollArea className="h-[300px] -mx-2">
              <div className="px-2 space-y-1">
                {/* Sezione Recenti */}
                {filteredSuppliers.recent.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 py-2 px-2">
                      <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Recenti
                      </span>
                    </div>
                    {filteredSuppliers.recent.map((rel: any) => (
                      <button
                        key={rel.id}
                        onClick={() => handleSelectSupplier(rel.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left group"
                        data-testid={`supplier-item-${rel.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Building2 className="h-5 w-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {rel.partner_company?.name || 'Fornitore'}
                          </p>
                          {rel.last_order_at && (
                            <p className="text-xs text-muted-foreground">
                              Ultimo ordine: {formatLastOrder(rel.last_order_at)}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </>
                )}
                
                {/* Sezione Altri fornitori */}
                {filteredSuppliers.others.length > 0 && (
                  <>
                    {filteredSuppliers.recent.length > 0 && (
                      <div className="flex items-center gap-2 py-2 px-2 mt-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                          Altri fornitori
                        </span>
                      </div>
                    )}
                    {filteredSuppliers.others.map((rel: any) => (
                      <button
                        key={rel.id}
                        onClick={() => handleSelectSupplier(rel.id)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left group"
                        data-testid={`supplier-item-${rel.id}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {rel.partner_company?.name || 'Fornitore'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Fornitore attivo
                          </p>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  </>
                )}
                
                {/* Nessun risultato */}
                {filteredSuppliers.recent.length === 0 && filteredSuppliers.others.length === 0 && supplierSearch && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Nessun fornitore trovato per "{supplierSearch}"</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          ) : (
            <div className="text-center py-8">
              <Building2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                Non hai relazioni attive.
              </p>
              <Button 
                variant="link" 
                onClick={() => {
                  setShowNewOrderDialog(false);
                  navigate('/directory');
                }}
                className="mt-2"
              >
                Vai alla Directory per trovare fornitori
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialog conferma eliminazione bozza */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questa bozza?</AlertDialogTitle>
            <AlertDialogDescription>
              Questa azione non può essere annullata. L'ordine verrà eliminato definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annulla</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteOrder}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OrdersPage;
